import { tv } from '@simplywallst/ui-core/next';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { INPUT_VALUE_RANGES } from '@/pages/Narrative/utils/validation';
import { VALUATOR_RATE_PRECISION } from '@/pages/Narrative/utils/schema';
import { parseNumberSafe } from '@/pages/Narrative/utils';
import {
  isValidNumber,
  processRealNumberInput,
  roundNumber,
  stripFractionsExceedingDecimals,
} from '@/pages/Narrative/utils/number';

export const field = tv({
  slots: {
    fieldset: 'group/field m-0 grid gap-x0_25',
    inputEl:
      'w-full select-none bg-transparent p-0 py-[5px] text-sm text-[--s-field-input-color-text] caret-brand-01',
    input: [
      '[.s-focus-kb_&]:focus-within:outline-dotted [.s-focus-kb_&]:focus-within:outline-2 [.s-focus-kb_&]:focus-within:outline-offset-2 [.s-focus-kb_&]:focus-within:outline-[--s-focus-color]',
      'group/input relative rounded-md text-sm',
      'border border-solid border-[--s-field-input-color-border]',
      'bg-[--s-field-input-color-bg]',
      'opacity-70 transition-opacity',
      'group-focus-within/field:bg-[--s-field-input-focus-color-bg] group-focus-within/field:opacity-100',
      'grid grid-cols-[1fr_max-content] items-center justify-center gap-x0_5 px-x1',
    ],
    inputSuffix: [
      'pointer-events-none end-x1_25 grid items-center',
      'fill-[--s-field-input-color-text] text-tiny font-strong opacity-70 transition-opacity group-focus-within/field:opacity-100',
    ],
  },
  variants: {
    hasError: {
      true: {
        input: 'border-[--s-field-input-error-color-border]',
      },
    },
  },
  defaultVariants: {
    hasError: false,
  },
});

type Props = {
  id: string;
  value: number;
  onChange: (value: number) => void;
};

const isWithinRange = (value: number) => {
  return (
    value >= INPUT_VALUE_RANGES.revenuePercentage.min &&
    value <= INPUT_VALUE_RANGES.revenuePercentage.max
  );
};

export const NarrativeMiniValuatorGrowthRevenueInput = ({
  id,
  value,
  onChange,
}: Props) => {
  const [input, setInput] = useState('');
  const inputNumber = parseNumberSafe(input, VALUATOR_RATE_PRECISION);
  const isValid = isValidNumber(input) && isWithinRange(inputNumber);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChangeManual = (event: ChangeEvent<HTMLInputElement>) => {
    const current = event.currentTarget.value;
    const newValue = stripFractionsExceedingDecimals(
      processRealNumberInput(current),
      VALUATOR_RATE_PRECISION
    );
    setInput(newValue);

    // Only emit changed value if doesn't differ from converted number ingested through useEffect below
    if (newValue === Number(newValue).toString() && isValidNumber(newValue)) {
      onChange(parseNumberSafe(newValue, VALUATOR_RATE_PRECISION));
    }
  };
  const handleBlur = () => {
    if (!isValid) {
      setInput(roundNumber(inputNumber, VALUATOR_RATE_PRECISION).toString());
      return;
    }

    onChange(inputNumber);
  };

  useEffect(() => {
    setInput(roundNumber(value, VALUATOR_RATE_PRECISION).toString());
  }, [value]);

  const styles = field({
    hasError: !isValid,
  });

  return (
    <fieldset className={styles.fieldset()}>
      <div className={styles.input()} onClick={() => inputRef.current?.focus()}>
        <input
          id={id}
          className={styles.inputEl()}
          type="tel"
          inputMode="decimal"
          value={input}
          onChange={handleChangeManual}
          onBlur={handleBlur}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
        <span className={styles.inputSuffix()}>%/yr</span>
      </div>
    </fieldset>
  );
};
